import { Container, createStyles, Text, Title } from '@mantine/core'
import { NextPage } from 'next'
import React from 'react'

const useStyles = createStyles((theme) => ({
  title: {
    textAlign: 'center',
    fontWeight: 900,
    fontSize: 38,
    marginBottom: theme.spacing.xl,

    [theme.fn.smallerThan('sm')]: {
      fontSize: 32
    }
  },
  subtitle: {
    textAlign: 'left',
    fontWeight: 700,
    fontSize: 24,
    marginBottom: theme.spacing.lg,

    [theme.fn.smallerThan('sm')]: {
      fontSize: 20
    }
  },
  text: {
    marginBottom: theme.spacing.md,
    lineHeight: 1.6
  },
  list: {
    marginBottom: theme.spacing.xs,
    paddingLeft: theme.spacing.lg
  }
}))

const TermsOfService: NextPage = () => {
  const { classes } = useStyles()

  return (
    <Container>
      <Title className={classes.title}>SWAP INTEGRATION TERMS OF SERVICE</Title>
      <Text className={classes.text}>Last Updated: July 19, 2023</Text>
      <ol className={classes.list}>
        <li className={classes.text}>Your Relationship With Us</li>
        <li className={classes.text}>Accepting the Terms</li>
        <li className={classes.text}>Changes to the Terms</li>
        <li className={classes.text}>Your Account with Us</li>
        <li className={classes.text}>Your Access to and Use of Our Services</li>
        <li className={classes.text}>Intellectual Property Rights</li>
        <li className={classes.text}>Content</li>
        <li className={classes.text}>Indemnity</li>
        <li className={classes.text}>Exclusions of Warranties</li>
        <li className={classes.text}>Limitation of Liability</li>
        <li className={classes.text}>Other Terms</li>
        <li className={classes.text}>Dispute Resolution</li>
        <li className={classes.text}>App Stores</li>
        <li className={classes.text}>Contact Us</li>
      </ol>
      <Title className={classes.subtitle}>1. Your Relationship With Us</Title>
      <Text className={classes.text}>
        The SWAP Integration App (the “Platform”) is provided by SWAP Integration, LLC (“SWAP Integration”).
      </Text>
      <Text className={classes.text}>
        You are reading the terms of service (the “Terms”), which govern the relationship and serve as an agreement
        between you and us and set forth the terms and conditions by which you may access and use the Platform and our
        related websites, services, applications, products and content (collectively, the “Services”). Access to certain
        Services or features of the Services (such as, by way of example and not limitation, the ability to submit or
        share User Content (defined below)) may be subject to age restrictions and not available to all users of the
        Services. Our Services are provided for private, non-commercial use. For purposes of these Terms, “you” and
        “your” means you as the user of the Services.
      </Text>
      <Text className={classes.text}>
        The Terms form a legally binding agreement between you and us. Please take the time to read them carefully. If
        you are under age 18, you may only use the Services with the consent of your parent or legal guardian. Please be
        sure your parent or legal guardian has reviewed and discussed these Terms with you.
      </Text>
      <Title className={classes.subtitle}>2. Accepting the Terms</Title>
      <Text className={classes.text}>
        By accessing or using our Services, you confirm that you can form a binding contract with SWAP Integration, that
        you accept these Terms and that you agree to comply with them. Your access to and use of our Services is also
        subject to our Privacy Policy (available at: https://www.swapintegration.com/privacy). By using the Services,
        you consent to the terms of the Privacy Policy.
      </Text>
      <Text className={classes.text}>
        If you are accessing or using the Services on behalf of a business or entity, then (a) “you” and “your” includes
        you and that business or entity, (b) you represent and warrant that you are an authorized representative of the
        business or entity with the authority to bind the entity to these Terms, and that you agree to these Terms on
        the entity’s behalf, and (c) your business or entity is legally and financially responsible for your access or
        use of the Services as well as for the access or use of your account by others affiliated with your entity,
        including any employees, agents or contractors.
      </Text>
      <Text className={classes.text}>
        You can accept the Terms by accessing or using our Services. You understand and agree that we will treat your
        access or use of the Services as acceptance of the Terms from that point onwards.
      </Text>
      <Text className={classes.text}>You should print and/or save a local copy of the Terms for your records.</Text>
      <Title className={classes.subtitle}>3. Changes to the Terms</Title>
      <Text className={classes.text}>
        We amend these Terms from time to time, for instance when we update the functionality of our Services, when we
        combine multiple apps or services operated by us or our affiliates into a single combined service or app, or
        when there are regulatory changes. We will use commercially reasonable efforts to generally notify all users of
        any material changes to these Terms, such as through a notice on our Platform, however, you should look at the
        Terms regularly to check for such changes. We will also update the “Last Updated” date at the top of these
        Terms, which reflect the effective date of such Terms. Your continued access or use of the Services after the
        date of the new Terms constitutes your acceptance of the new Terms. If you do not agree to the new Terms, you
        must stop accessing or using the Services. The terms will remain accessible in their most updated form at
        https://www.swapintegration.com/termsofservice
      </Text>
      <Title className={classes.subtitle}>4. Your Account with Us</Title>
      <Text className={classes.text}>
        To access or use some of our Services, you must create an account with us. When you create this account, you
        must provide accurate and up-to-date information. It is important that you maintain and promptly update your
        details and any other information you provide to us, to keep such information current and complete.
      </Text>
      <Text className={classes.text}>
        It is important that you keep your account password confidential and that you do not disclose it to any third
        party. If you know or suspect that any third party knows your password or has accessed your account, you must
        notify us immediately at: https://www.swapintegration.com/contact.
      </Text>
      <Text className={classes.text}>
        You agree that you are solely responsible (to us and to others) for the activity that occurs under your account.
      </Text>
      <Text className={classes.text}>
        In case of your breach of these Terms, we reserve the right to disable your user account at any time, including
        if in our judgment you have failed to comply with any of the provisions of these Terms, or if activities occur
        on your account which, in our sole discretion, would or might cause damage to or impair the Services or infringe
        or violate any third party rights, or violate any applicable laws or regulations.
      </Text>
      <Text className={classes.text}>
        If you no longer want to use our Services again, and would like your account deleted, contact us at:
        https://www.swapintegration.com/contact. We will provide you with further assistance and guide you through the
        process. Once you choose to delete your account, you will not be able to reactivate your account or retrieve any
        of the content or information you have added.
      </Text>
      <Title className={classes.subtitle}>5. Your Access to and Use of Our Services</Title>
      <Text className={classes.text}>
        Your access to and use of the Services is subject to these Terms and all applicable laws and regulations. You
        may not:
      </Text>
      <ul className={classes.list}>
        <li className={classes.text}>
          access or use the Services if you are not fully able and legally competent to agree to these Terms or are
          authorized to use the Services by your parent or legal guardian;
        </li>
        <li className={classes.text}>
          make unauthorized copies, modify, adapt, translate, reverse engineer, disassemble, decompile or create any
          derivative works of the Services or any content included therein, including any files, tables or documentation
          (or any portion thereof) or determine or attempt to determine any source code, algorithms, methods or
          techniques embodied by the Services or any derivative works thereof;
        </li>
        <li className={classes.text}>
          distribute, license, transfer, or sell, in whole or in part, any of the Services or any derivative works
          thereof;
        </li>
        <li className={classes.text}>
          market, rent or lease the Services for a fee or charge, or use the Services to advertise or perform any
          commercial solicitation;
        </li>
        <li className={classes.text}>
          use the Services, without our express written consent, for any commercial or unauthorized purpose, including
          communicating or facilitating any commercial advertisement or solicitation or spamming;
        </li>
        <li className={classes.text}>
          interfere with or attempt to interfere with the proper working of the Services, disrupt our website or any
          networks connected to the Services, or bypass any measures we may use to prevent or restrict access to the
          Services;
        </li>
        <li className={classes.text}>
          incorporate the Services or any portion thereof into any other program or product. In such case, we reserve
          the right to refuse service, terminate accounts or limit access to the Services in our sole discretion;
        </li>
        <li className={classes.text}>
          use automated scripts to collect information from or otherwise interact with the Services;
        </li>
        <li className={classes.text}>
          impersonate any person or entity, or falsely state or otherwise misrepresent you or your affiliation with any
          person or entity, including giving the impression that any content you upload, post, transmit, distribute or
          otherwise make available emanates from the Services;
        </li>
        <li className={classes.text}>
          intimidate or harass another, or promote sexually explicit material, violence or discrimination based on race,
          sex, religion, nationality, disability, sexual orientation or age;
        </li>
        <li className={classes.text}>
          use or attempt to use another’s account, service or system without authorization from SWAP Integration, or
          create a false identity on the Services;
        </li>
        <li className={classes.text}>
          use the Services in a manner that may create a conflict of interest or undermine the purposes of the Services,
          such as trading reviews with other users or writing or soliciting fake reviews;
        </li>
        <li className={classes.text}>
          use the Services to upload, transmit, distribute, store or otherwise make available in any way: files that
          contain viruses, trojans, worms, logic bombs or other material that is malicious or technologically harmful;
        </li>
        <li className={classes.text}>
          any unsolicited or unauthorized advertising, solicitations, promotional materials, “junk mail,” “spam,” “chain
          letters,” “pyramid schemes,” or any other prohibited form of solicitation;
        </li>
        <li className={classes.text}>
          any private information of any third party, including addresses, phone numbers, email addresses, number and
          feature in the personal identity document (e.g., National Insurance numbers, passport numbers) or credit card
          numbers;
        </li>
        <li className={classes.text}>
          any material which does or may infringe any copyright, trademark or other intellectual property or privacy
          rights of any other person;
        </li>
        <li className={classes.text}>
          any material which is defamatory of any person, obscene, offensive, pornographic, hateful or inflammatory;
        </li>
        <li className={classes.text}>
          any material that would constitute, encourage or provide instructions for a criminal offence, dangerous
          activities or self-harm;
        </li>
        <li className={classes.text}>
          any material that is deliberately designed to provoke or antagonize people, especially trolling and bullying,
          or is intended to harass, harm, hurt, scare, distress, embarrass or upset people;
        </li>
        <li className={classes.text}>
          any material that contains a threat of any kind, including threats of physical violence;
        </li>
        <li className={classes.text}>
          any material that is racist or discriminatory, including discrimination on the basis of someone’s race,
          religion, age, gender, disability or sexuality;
        </li>
        <li className={classes.text}>
          any answers, responses, comments, opinions, analysis or recommendations that you are not properly licensed or
          otherwise qualified to provide; or
        </li>
        <li className={classes.text}>
          material that, in the sole judgment of SWAP Integration, is objectionable or which restricts or inhibits any
          other person from using the Services, or which may expose SWAP Integration, the Services or its users to any
          harm or liability of any type.
        </li>
      </ul>
      <Text className={classes.text}>
        In addition to the above, your access to and use of the Services must, at all times, be compliant with our
        Privacy Policy.
      </Text>
      <Text className={classes.text}>
        We reserve the right, at any time and without prior notice, to remove or disable access to functionality and/or
        content associated with the Services at our discretion for any reason or no reason. Some of the reasons we may
        remove or disable access to content may include finding the content objectionable, in violation of these Terms
        or our Privacy Policy, or otherwise harmful to the Services or our users. We may utilize automated systems to
        analyze your content (including emails) to provide you with personally relevant product features, such as
        customized search results, tailored advertising, and spam and malware detection. This analysis may occur as any
        information generated by you and transmitted to the Services is sent or received, and when it is stored.
      </Text>
      <Title className={classes.subtitle}>6. Intellectual Property Rights</Title>
      <Text className={classes.text}>
        We respect intellectual property rights and ask you to do the same. As a condition of your access to and use of
        the Services, you agree to maintain the integrity of and not infringe any intellectual property rights,
        including copyrights, patents and trademarks, owned by SWAP Integration. You agree not to utilize the Services
        to infringe upon or otherwise diminish the intellectual property rights owned by SWAP Integration or any other
        party.
      </Text>
      <Title className={classes.subtitle}>7. Content</Title>
      <Title className={classes.subtitle}> SWAP Integration Content</Title>
      <Text className={classes.text}>
        As between you and SWAP Integration, all content, software, images, text, graphics, illustrations, logos,
        patents, trademarks, service marks, copyrights, photographs, audio, videos, music on and “look and feel” of the
        Services, and all intellectual property rights related thereto (the “SWAP Integration Content”), are either
        owned or licensed by SWAP Integration, it being understood that you or your licensors will own any User Content
        (as defined below) you upload or transmit through the Services. Use of the SWAP Integration Content or materials
        on the Services for any purpose not expressly permitted by these Terms is strictly prohibited. Such content may
        not be downloaded, copied, reproduced, distributed, transmitted, broadcast, displayed, sold, licensed, or
        otherwise exploited for any purpose whatsoever without our or, where applicable, our licensors’ prior written
        consent. We and our licensors reserve all rights not expressly granted in and to their content.
      </Text>
      <Text className={classes.text}>
        You acknowledge and agree that we may generate revenues, increase goodwill or otherwise increase our value from
        your use of the Services, including, by way of example and not limitation, through the sale of advertising,
        sponsorships, promotions, usage data and Gifts (defined below), and except as specifically permitted by us in
        these Terms or in another agreement you enter into with us, you will have no right to share in any such revenue,
        goodwill or value whatsoever.
      </Text>

      <Text className={classes.text}>
        Subject to the terms and conditions of the Terms, you are hereby granted a non-exclusive, limited,
        non-transferable, non-sublicensable, revocable, worldwide license to access and use the Services, and to access
        the SWAP Integration Content through your use of the Services and solely in compliance with these Terms. SWAP
        Integration reserves all rights not expressly granted herein in the Services and the SWAP Integration Content.
        You acknowledge and agree that SWAP Integration may terminate this license at any time for any reason or no
        reason.
      </Text>

      <Text className={classes.text}>
        You acknowledge and agree that in the unlikely case that you view content provided by others on the Services,
        you are doing so at your own risk. You agree to treat any content on our Services as being provided for general
        information only. It is not intended to amount to advice on which you should rely. You must obtain professional
        or specialist advice before taking, or refraining from, any action on the basis of the content on our Services.
        These Terms do not encompass any professional or specialist advice, and SWAP Integration does not in any way
        purport or suggest to provide professional or specialist advice under these Terms or the use of the Services.
        Any professional or specialist advice shall only be provided under a separate written agreement.
      </Text>
      <Text className={classes.text}>
        We make no representations, warranties or guarantees, whether express or implied, that any SWAP Integration
        Content (including User Content) is accurate, complete or up to date. Where our Services contain links to other
        sites and resources provided by third parties, these links are provided for your information only. We have no
        control over the contents of those sites or resources. Such links should not be interpreted as approval by us of
        those linked websites or information you may obtain from them. You acknowledge that we have no obligation to
        pre-screen, monitor, review, or edit any content posted by you and other users on the Services (including User
        Content).
      </Text>
      <Title className={classes.subtitle}>User-Generated Content</Title>
      <Text className={classes.text}>
        Users of the Services may from time to time be permitted to upload, post or transmit or otherwise make available
        content through the Services including, without limitation, any text, data, photographs, images, user videos,
        and sound recordings embodied therein (“User Content”). Users of the Services may from time to time also extract
        all or any portion of User Content created by another user to produce additional User Content, including
        collaborative User Content with other users, that combine and intersperse User Content generated by more than
        one user. Users of the Services may also overlay music, graphics, stickers, and other elements provided by SWAP
        Integration (“SWAP Integration Elements”) onto this User Content and transmit this User Content through the
        Services. The information and materials in the User Content, including User Content that includes SWAP
        Integration Elements, have not been endorsed, verified or approved by SWAP Integration. The views expressed by
        other users on the Services, including via the entry of any text or other information in associated with any
        messaging features of the Services, are not endorsed by SWAP Integration and do not represent our views or
        values.
      </Text>
      <Text className={classes.text}>
        Whenever you access or use a feature that allows you to upload or transmit User Content through the Services
        (including via certain third party social media platforms such as Instagram, Facebook, YouTube, Twitter), or to
        make contact with other users of the Services, you must comply with the Terms and Privacy Policy. You may also
        choose to upload or transmit your User Content, including User Content that includes SWAP Integration Elements,
        on sites or platforms hosted by third parties. If you decide to do this, you must comply with their content
        guidelines and comply with these Terms. As noted above, these features may not be available to all or any users
        of the Services at present or in the future, and we have no liability to you for limiting your right to certain
        features of the Services.
      </Text>

      <Text className={classes.text}>
        Any User Content will be considered non-confidential and non-proprietary. You must not post any User Content on
        or through the Services or transmit to us any User Content that you consider to be confidential or proprietary.
        When you submit User Content through the Services, you agree and represent that you own that User Content, or
        you have received all necessary permissions, clearances from, or are authorized by, the owner of any part of the
        content to submit it to the Services, to transmit it from the Services to other third-party platforms, and/or
        adopt any third-party content.
      </Text>
      <Text className={classes.text}>
        You or the owner of your User Content still own the copyright in User Content sent to us, but by submitting User
        Content via the Services, you hereby grant us an unconditional irrevocable, non-exclusive, royalty-free, fully
        transferable, perpetual worldwide license to use, modify, adapt, reproduce, make derivative works of, publish
        and/or transmit, and/or distribute and to authorize other users of the Services and other third-parties to view,
        access, use, download, modify, adapt, reproduce, make derivative works of, publish and/or transmit your User
        Content in any format and on any platform, either now known or hereinafter invented.
      </Text>

      <Text className={classes.text}>
        Waiver of Rights to User Content. By posting User Content to or through the Services, you waive any rights to
        prior inspection or approval of any marketing or promotional materials related to such User Content. You also
        waive any and all rights of privacy, publicity, or any other rights of a similar nature in connection with your
        User Content, or any portion thereof. To the extent any moral rights are not transferable or assignable, you
        hereby waive and agree never to assert any and all moral rights, or to support, maintain or permit any action
        based on any moral rights that you may have in or with respect to any User Content you Post to or through the
        Services.
      </Text>

      <Text className={classes.text}>
        We also have the right to disclose your identity to any third party who is claiming that any User Content posted
        or uploaded by you to our Services constitutes a violation of their intellectual property rights, or of their
        right to privacy.
      </Text>

      <Text className={classes.text}>
        We have the right – but not the obligation – in our sole discretion to remove, disallow, block or delete any
        User Content (i) that we consider to violate these Terms, or (ii) in response to complaints from other users or
        third parties, with or without notice and without any liability to you. As a result, we recommend that you save
        copies of any User Content that you post to the Services on your personal device(s) in the event that you want
        to ensure that you have permanent access to copies of such User Content. We do not guarantee the accuracy,
        integrity, appropriateness or quality of any User Content, and under no circumstances will we be liable in any
        way for any User Content.
      </Text>

      <Text className={classes.text}>
        You or an admin user of an entity that maintains an account on the Services control whether your User Content is
        made publicly available on the Services to all other users of the Services or only available to people you
        approve. To restrict access to your User Content, you should select the privacy setting available within the
        Platform.
      </Text>
      <Text className={classes.text}>
        We accept no liability in respect of any content submitted by users and published by us or by authorized third
        parties.
      </Text>

      <Text className={classes.text}>
        If you wish to file a complaint about information or materials uploaded by other users, contact us at:
        https://www.swapintegration.com/contact.
      </Text>
      <Text className={classes.text}>
        SWAP Integration takes reasonable measures to expeditiously remove from our Services any infringing material
        that we become aware of. It is SWAP Integration’s policy, in appropriate circumstances and at its discretion, to
        disable or terminate the accounts of users of the Services who repeatedly infringe copyrights or intellectual
        property rights of others.
      </Text>

      <Text className={classes.text}>
        While our own staff is continually working to develop and evaluate our own product ideas and features, we pride
        ourselves on paying close attention to the interests, feedback, comments, and suggestions we receive from the
        user community. If you choose to contribute by sending us or our employees any ideas for products, services,
        features, modifications, enhancements, content, refinements, technologies, content offerings (such as audio,
        visual, games, or other types of content), promotions, strategies, or product/feature names, or any related
        documentation, artwork, computer code, diagrams, or other materials (collectively “Feedback”), then regardless
        of what your accompanying communication may say, the following terms will apply, so that future
        misunderstandings can be avoided. Accordingly, by sending Feedback to us, you agree that:
      </Text>

      <Text className={classes.text}>
        SWAP Integration has no obligation to review, consider, or implement your Feedback, or to return to you all or
        part of any Feedback for any reason;
      </Text>

      <Text className={classes.text}>
        Feedback is provided on a non-confidential basis, and we are not under any obligation to keep any Feedback you
        send confidential or to refrain from using or disclosing it in any way; and
      </Text>

      <Text className={classes.text}>
        You irrevocably grant us perpetual and unlimited permission to reproduce, distribute, create derivative works
        of, modify, publicly perform (including on a through-to-the-audience basis), communicate to the public, make
        available, publicly display, and otherwise use and exploit the Feedback and derivatives thereof for any purpose
        and without restriction, free of charge and without attribution of any kind, including by making, using,
        selling, offering for sale, importing, and promoting commercial products and services that incorporate or embody
        Feedback, whether in whole or in part, and whether as provided or as modified.
      </Text>
      <Title className={classes.subtitle}>8. Indemnity</Title>
      <Text className={classes.text}>
        You agree to defend, indemnify, and hold harmless SWAP Integration, its parents, subsidiaries, and affiliates,
        and each of their respective officers, directors, employees, agents and advisors from any and all claims,
        liabilities, costs, and expenses, including, but not limited to, attorneys’ fees and expenses, arising out of a
        breach by you or any user of your account of these Terms or arising out of a breach of your obligations,
        representation and warranties under these Terms.
      </Text>
      <Title className={classes.subtitle}>9. EXCLUSION OF WARRANTIES</Title>
      <Text className={classes.text}>
        NOTHING IN THESE TERMS SHALL AFFECT ANY STATUTORY RIGHTS THAT YOU CANNOT CONTRACTUALLY AGREE TO ALTER OR WAIVE
        AND ARE LEGALLY ALWAYS ENTITLED TO AS A CONSUMER.
      </Text>
      <Text className={classes.text}>
        THE SERVICES ARE PROVIDED “AS IS” AND WE MAKE NO WARRANTY OR REPRESENTATION TO YOU WITH RESPECT TO THEM. IN
        PARTICULAR WE DO NOT REPRESENT OR WARRANT TO YOU THAT:
      </Text>
      <Text className={classes.text}>YOUR USE OF THE SERVICES WILL MEET YOUR REQUIREMENTS;</Text>
      <Text className={classes.text}>
        YOUR USE OF THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE OR FREE FROM ERROR;
      </Text>
      <Text className={classes.text}>
        ANY INFORMATION OBTAINED BY YOU AS A RESULT OF YOUR USE OF THE SERVICES WILL BE ACCURATE OR RELIABLE; AND
      </Text>
      <Text className={classes.text}>
        DEFECTS IN THE OPERATION OR FUNCTIONALITY OF ANY SOFTWARE PROVIDED TO YOU AS PART OF THE SERVICES WILL BE
        CORRECTED.
      </Text>
      <Text className={classes.text}>
        NO CONDITIONS, WARRANTIES OR OTHER TERMS (INCLUDING ANY IMPLIED TERMS AS TO SATISFACTORY QUALITY, FITNESS FOR
        PURPOSE OR CONFORMANCE WITH DESCRIPTION) APPLY TO THE SERVICES EXCEPT TO THE EXTENT THAT THEY ARE EXPRESSLY SET
        OUT IN THE TERMS. WE MAY CHANGE, SUSPEND, WITHDRAW OR RESTRICT THE AVAILABILITY OF ALL OR ANY PART OF OUR
        PLATFORM FOR BUSINESS AND OPERATIONAL REASONS AT ANY TIME WITHOUT NOTICE.
      </Text>
      <Title className={classes.subtitle}>10. LIMITATION OF LIABILITY</Title>
      <Text className={classes.text}>
        NOTHING IN THESE TERMS SHALL EXCLUDE OR LIMIT OUR LIABILITY FOR LOSSES WHICH MAY NOT BE LAWFULLY EXCLUDED OR
        LIMITED BY APPLICABLE LAW. THIS INCLUDES LIABILITY FOR DEATH OR PERSONAL INJURY CAUSED BY OUR NEGLIGENCE OR THE
        NEGLIGENCE OF OUR EMPLOYEES, AGENTS OR SUBCONTRACTORS AND FOR FRAUD OR FRAUDULENT MISREPRESENTATION.
      </Text>
      <Text className={classes.text}>SUBJECT TO THE PARAGRAPH ABOVE, WE SHALL NOT BE LIABLE TO YOU FOR:</Text>
      <ul className={classes.list}>
        <li className={classes.text}>ANY LOSS OF PROFIT (WHETHER INCURRED DIRECTLY OR INDIRECTLY);</li>
        <li className={classes.text}>ANY LOSS OF GOODWILL;</li>
        <li className={classes.text}>ANY LOSS OF OPPORTUNITY;</li>
        <li className={classes.text}>ANY LOSS OF DATA SUFFERED BY YOU; OR</li>
        <li className={classes.text}>
          ANY INDIRECT OR CONSEQUENTIAL LOSSES WHICH MAY BE INCURRED BY YOU. ANY OTHER LOSS WILL BE LIMITED TO THE
          AMOUNT PAID BY YOU TO SWAP INTEGRATION WITHIN THE LAST 12 MONTHS.
        </li>
      </ul>
      <Text className={classes.text}>ANY LOSS OR DAMAGE WHICH MAY BE INCURRED BY YOU AS A RESULT OF:</Text>
      <ul className={classes.list}>
        <li className={classes.text}>
          ANY RELIANCE PLACED BY YOU ON THE COMPLETENESS, ACCURACY OR EXISTENCE OF ANY ADVERTISING, OR AS A RESULT OF
          ANY RELATIONSHIP OR TRANSACTION BETWEEN YOU AND ANY ADVERTISER OR SPONSOR WHOSE ADVERTISING APPEARS ON THE
          SERVICE;
        </li>
        <li className={classes.text}>
          ANY CHANGES WHICH WE MAY MAKE TO THE SERVICES, OR FOR ANY PERMANENT OR TEMPORARY CESSATION IN THE PROVISION OF
          THE SERVICES (OR ANY FEATURES WITHIN THE SERVICES);
        </li>
        <li className={classes.text}>
          THE DELETION OF, CORRUPTION OF, OR FAILURE TO STORE, ANY CONTENT AND OTHER COMMUNICATIONS DATA MAINTAINED OR
          TRANSMITTED BY OR THROUGH YOUR USE OF THE SERVICES;
        </li>
        <li className={classes.text}>YOUR FAILURE TO PROVIDE US WITH ACCURATE ACCOUNT INFORMATION; OR</li>
        <li className={classes.text}>YOUR FAILURE TO KEEP YOUR PASSWORD OR ACCOUNT DETAILS SECURE AND CONFIDENTIAL.</li>
      </ul>
      <Text className={classes.text}>
        WE HAVE NO LIABILITY TO YOU FOR ANY LOSS OF PROFIT, LOSS OF BUSINESS, LOSS OF GOODWILL OR BUSINESS REPUTATION,
        BUSINESS INTERRUPTION, OR LOSS OF BUSINESS OPPORTUNITY.
      </Text>
      <Text className={classes.text}>
        IF DEFECTIVE DIGITAL CONTENT THAT WE HAVE SUPPLIED DAMAGES A DEVICE OR DIGITAL CONTENT BELONGING TO YOU AND THIS
        IS CAUSED BY OUR FAILURE TO USE REASONABLE CARE AND SKILL, WE WILL EITHER REPAIR THE DAMAGE OR PAY YOU
        COMPENSATION. HOWEVER, WE WILL NOT BE LIABLE FOR DAMAGE THAT YOU COULD HAVE AVOIDED BY FOLLOWING OUR ADVICE TO
        APPLY AN UPDATE OFFERED TO YOU FREE OF CHARGE OR FOR DAMAGE THAT WAS CAUSED BY YOU FAILING TO CORRECTLY FOLLOW
        INSTALLATION INSTRUCTIONS OR TO HAVE IN PLACE THE MINIMUM SYSTEM REQUIREMENTS ADVISED BY US.
      </Text>
      <Text className={classes.text}>
        THESE LIMITATIONS ON OUR LIABILITY TO YOU SHALL APPLY WHETHER OR NOT WE HAVE BEEN ADVISED OF OR SHOULD HAVE BEEN
        AWARE OF THE POSSIBILITY OF ANY SUCH LOSSES ARISING.
      </Text>
      <Text className={classes.text}>
        YOU ARE RESPONSIBLE FOR ANY MOBILE CHARGES THAT MAY APPLY TO YOUR USE OF OUR SERVICE, INCLUDING TEXT-MESSAGING
        AND DATA CHARGES. IF YOU’RE UNSURE WHAT THOSE CHARGES MAY BE, YOU SHOULD ASK YOUR SERVICE PROVIDER BEFORE USING
        THE SERVICE.
      </Text>
      <Text className={classes.text}>
        TO THE FULLEST EXTENT PERMITTED BY LAW, ANY DISPUTE YOU HAVE WITH ANY THIRD PARTY ARISING OUT OF YOUR USE OF THE
        SERVICES, INCLUDING, BY WAY OF EXAMPLE AND NOT LIMITATION, ANY CARRIER, COPYRIGHT OWNER OR OTHER USER, IS
        DIRECTLY BETWEEN YOU AND SUCH THIRD PARTY, AND YOU IRREVOCABLY RELEASE US AND OUR AFFILIATES FROM ANY AND ALL
        CLAIMS, DEMANDS AND DAMAGES (ACTUAL AND CONSEQUENTIAL) OF EVERY KIND AND NATURE, KNOWN AND UNKNOWN, ARISING OUT
        OF OR IN ANY WAY CONNECTED WITH SUCH DISPUTES.
      </Text>
      <Title className={classes.subtitle}>11. Other Terms</Title>
      <Text className={classes.text}>
        <strong>Open Source.</strong> The Platform may contain certain open source software. Each item of open source
        software is subject to its own applicable license terms.
      </Text>
      <Text className={classes.text}>
        <strong>Entire Agreement.</strong> These Terms constitute the whole legal agreement between you and SWAP
        Integration and govern your use of the Services and completely replace any prior agreements between you and SWAP
        Integration in relation to the Services.
      </Text>
      <Text className={classes.text}>
        <strong>Links.</strong> You may link to our home page, provided you do so in a way that is fair and legal and
        does not damage our reputation or take advantage of it. You must not establish a link in such a way as to
        suggest any form of association, approval or endorsement on our part where none exists. You must not establish a
        link to our Services in any website that is not owned by you. We reserve the right to withdraw linking
        permission without notice.
      </Text>
      <Text className={classes.text}>
        <strong>No Waiver.</strong> Our failure to insist upon or enforce any provision of these Terms shall not be
        construed as a waiver of any provision or right.
      </Text>
      <Text className={classes.text}>
        <strong>Security.</strong> We do not guarantee that our Services will be secure or free from bugs or viruses.
        You are responsible for configuring your information technology, computer programs and platform to access our
        Services. You should use your own virus protection software.
      </Text>
      <Text className={classes.text}>
        <strong>Severability.</strong> If any court of law, having jurisdiction to decide on this matter, rules that any
        provision of these Terms is invalid, then that provision will be removed from the Terms without affecting the
        rest of the Terms, and the remaining provisions of the Terms will continue to be valid and enforceable.
      </Text>
      <Text className={classes.text}>
        <strong>Counter-notice.</strong> If a counter-notice is received by SWAP Integration’s Copyright Agent, we may
        send a copy of the counter-notice to the original complaining party informing that person that we may replace
        the removed content or cease disabling it. Unless the original complaining party files an action seeking a court
        order, member or user, the removed content may be replaced, or access to it restored, in ten business days or
        more after receipt of the counter-notice, at SWAP Integration’s sole discretion.
      </Text>
      <Text className={classes.text}>
        Please understand that filing a counter-notification may lead to legal proceedings between you and the
        complaining party to determine ownership. Be aware that there may be adverse legal consequences in your country
        if you make a false or bad faith allegation by using this process.
      </Text>
      <Text className={classes.text}>
        <strong>California Consumer Rights Notice.</strong> Under California Civil Code Section 1789.3, California users
        of the Services receive the following specific consumer rights notice: The Complaint Assistance Unit of the
        Division of Consumer Services of the California Department of Consumer Affairs may be contacted in writing at
        the contact information set forth at{' '}
        <a href='https://www.dca.ca.gov/about_us/contactus.shtml'>https://www.dca.ca.gov/about_us/contactus.shtml</a>.
      </Text>
      <Text className={classes.text}>
        Users of the Services who are California residents and are under 18 years of age may request and obtain removal
        of User Content they posted by contacting us at:{' '}
        <a href='https://www.swapintegration.com/contact'>https://www.swapintegration.com/contact</a>. All requests must
        be labeled &ldquo;California Removal Request&ldquo; on the email subject line. All requests must provide a
        description of the User Content you want removed and information reasonably sufficient to permit us to locate
        that User Content. We do not accept California Removal Requests via postal mail, telephone or facsimile. We are
        not responsible for notices that are not labeled or sent properly, and we may not be able to respond if you do
        not provide adequate information.
      </Text>
      <Text className={classes.text}>
        <strong>Exports.</strong> You agree that you will not export or re-export, directly or indirectly the Services
        and/or other information or materials provided by SWAP Integration hereunder, to any country for which the
        United States or any other relevant jurisdiction requires any export license or other governmental approval at
        the time of export without first obtaining such license or approval. In particular, but without limitation, the
        Services may not be exported or re-exported (a) into any U.S. embargoed countries or any country that has been
        designated by the U.S. Government as a “terrorist supporting” country, or (b) to anyone listed on any U.S.
        Government list of prohibited or restricted parties, including the U.S. Treasury Department&apos;s list of
        Specially Designated Nationals or the U.S. Department of Commerce Denied Person’s List or Entity List.
      </Text>
      <Text className={classes.text}>
        <strong>U.S. Government Restricted Rights.</strong> The Services and related documentation are &ldquo;Commercial
        Items&ldquo;, as that term is defined at 48 C.F.R. §2.101, consisting of &ldquo;Commercial Computer
        Software&ldquo; and &ldquo;Commercial Computer Software Documentation&ldquo;, as such terms are used in 48
        C.F.R. §12.212 or 48 C.F.R. §227.7202, as applicable. Consistent with 48 C.F.R. §12.212 or 48 C.F.R. §227.7202-1
        through 227.7202-4, as applicable, the Commercial Computer Software and Commercial Computer Software
        Documentation are being licensed to U.S. Government end users (a) only as Commercial Items and (b) with only
        those rights as are granted to all other end users pursuant to the terms and conditions herein.
      </Text>
      <Title className={classes.subtitle}>12. Dispute Resolution</Title>
      <Title className={classes.subtitle}>A. Informal resolution process first.</Title>
      <Text className={classes.text}>
        If we have a dispute with you relating to or arising out of these Terms, we will first try and resolve it with
        you amicably. You agree to do the same for us. To be clear, when we use the terms “SWAP Integration,” “we,” or
        “us” in this Section 12.A, we mean SWAP Integration, LLC and all of our affiliated companies and individuals.
      </Text>
      <Text className={classes.text}>
        The party raising a dispute will initiate this process by notifying the other. Whichever party receives the
        notice will have 60 days to respond. If the dispute has not been resolved after the response time has expired,
        or within 30 days after a response has been issued, whichever is earlier, either party may file legal action
        against the other. Engaging in this informal dispute resolution process is a requirement that must be completed
        before filing any legal action. You and SWAP Integration agree that you both will make a good faith effort to
        resolve the dispute amicably before either you or SWAP Integration files any legal action against the other, and
        that the statute of limitations and any filing fee deadlines shall be tolled while the parties engage in the
        informal dispute resolution process.
      </Text>
      <Title className={classes.subtitle}>B. Exclusive venue.</Title>
      <Text className={classes.text}>
        These Terms and any claims, causes of action, of any kind or character, or demand arising out of or relating to
        the Terms will be governed by the laws of the State of Colorado. Any claim, cause of action or dispute, arising
        out of or relating to these Terms shall also be resolved exclusively in the U.S. District Court for Colorado or
        the Supreme Court of the State of Colorado in Denver County or any other venue in Denver County for which the
        Supreme Court of the State of Colorado is the court of last resort. You also agree to submit to the personal
        jurisdiction of either of these courts for the purpose of litigating any such claim.
      </Text>
      <Title className={classes.subtitle}>C. One year limitation period / limitation on legal action.</Title>
      <Text className={classes.text}>
        YOU AND SWAP INTEGRATION AGREE THAT YOU MUST INITIATE ANY PROCEEDING OR ACTION WITHIN ONE (1) YEAR OF THE DATE
        OF THE OCCURRENCE OF THE EVENT OR FACTS GIVING RISE TO A DISPUTE THAT IS ARISING OUT OF OR RELATED TO THESE
        TERMS. OTHERWISE, YOU FOREVER WAIVE THE RIGHT TO PURSUE ANY CLAIM OR CAUSE OF ACTION, OF ANY KIND OR CHARACTER,
        BASED ON SUCH EVENTS OR FACTS, AND SUCH CLAIM(S) OR CAUSE(S) OF ACTION ARE PERMANENTLY BARRED.
      </Text>
      <Title className={classes.subtitle}>13. App Stores</Title>

      <Text className={classes.text}>
        To the extent permitted by applicable law, the following supplemental terms shall apply when accessing the
        Platform through specific devices:
      </Text>
      <Text className={classes.text}>
        <strong>Notice regarding Apple.</strong>
      </Text>
      <Text className={classes.text}>
        By downloading the Platform from a device made by Apple, Inc. (“Apple”) or from Apple’s App Store, you
        specifically acknowledge and agree that:
      </Text>
      <Text className={classes.text}>
        These Terms between SWAP Integration and you; Apple is not a party to these Terms.
      </Text>
      <Text className={classes.text}>
        The license granted to you hereunder is limited to a personal, limited, non-exclusive, non-transferable right to
        install the Platform on the Apple device(s) authorised by Apple that you own or control for personal,
        non-commercial use, subject to the Usage Rules set forth in Apple’s App Store Terms of Services.
      </Text>
      <Text className={classes.text}>
        Apple is not responsible for the Platform or the content thereof and has no obligation whatsoever to furnish any
        maintenance or support services with respect to the Platform.
      </Text>
      <Text className={classes.text}>
        In the event of any failure of the Platform to conform to any applicable warranty, you may notify Apple, and
        Apple will refund the purchase price for the Platform, if any, to you. To the maximum extent permitted by
        applicable law, Apple will have no other warranty obligation whatsoever with respect to the Platform.
      </Text>
      <Text className={classes.text}>
        Apple is not responsible for addressing any claims by you or a third party relating to the Platform or your
        possession or use of the Platform, including without limitation (a) product liability claims; (b) any claim that
        the Platform fails to conform to any applicable legal or regulatory requirement; and (c) claims arising under
        consumer protection or similar legislation.
      </Text>
      <Text className={classes.text}>
        In the event of any third party claim that the Platform or your possession and use of the Platform infringes
        such third party’s intellectual property rights, Apple is not responsible for the investigation, defence,
        settlement or discharge of such intellectual property infringement claim.
      </Text>
      <Text className={classes.text}>
        You represent and warrant that (a) you are not located in a country that is subject to a U.S. Government
        embargo, or that has been designated by the U.S. Government as a “terrorist supporting” country; and (b) you are
        not listed on any U.S. Government list of prohibited or restricted parties.
      </Text>
      <Text className={classes.text}>
        Apple and its subsidiaries are third party beneficiaries of these Terms and upon your acceptance of the terms
        and conditions of these Terms, Apple will have the right (and will be deemed to have accepted the right) to
        enforce these Terms against you as a third party beneficiary hereof.
      </Text>
      <Text className={classes.text}>
        SWAP Integration expressly authorizes use of the Platform by multiple users through the Family Sharing or any
        similar functionality provided by Apple.
      </Text>
      <Text className={classes.text}>
        <strong>Windows Phone Store.</strong>
      </Text>
      <Text className={classes.text}>
        By downloading the Platform from the Windows Phone Store (or its successors) operated by Microsoft, Inc. or its
        affiliates, you specifically acknowledge and agree that:
      </Text>
      <Text className={classes.text}>
        You may install and use one copy of the Platform on up to five (5) Windows Phone enabled devices that are
        affiliated with the Microsoft account you use to access the Windows Phone Store. Beyond that, we reserve the
        right to apply additional conditions or charge additional fees.
      </Text>
      <Text className={classes.text}>
        You acknowledge that Microsoft Corporation, your phone manufacturer and network operator have no obligation
        whatsoever to furnish any maintenance and support services with respect to the Platform.
      </Text>
      <Text className={classes.text}>
        <strong>Amazon Appstore.</strong>
      </Text>
      <Text className={classes.text}>
        By downloading the Platform from the Amazon Appstore (or its successors) operated by Amazon Digital Services,
        Inc. or affiliates (“Amazon”), you specifically acknowledge and agree that:
      </Text>
      <Text className={classes.text}>
        to the extent of any conflict between (a) the Amazon Appstore Terms of Use or such other terms which Amazon
        designates as default end user license terms for the Amazon Appstore (“Amazon Appstore EULA Terms”), and (b) the
        other terms and conditions in these Terms, the Amazon Appstore EULA Terms shall apply with respect to your use
        of the Platform that you download from the Amazon Appstore, and
      </Text>
      <Text className={classes.text}>
        Amazon does not have any responsibility or liability related to compliance or non-compliance by SWAP Integration
        or you (or any other user) under these Terms or the Amazon Appstore EULA Terms.
      </Text>
      <Text className={classes.text}>
        <strong>Google Play.</strong>
      </Text>
      <Text className={classes.text}>
        By downloading the Platform from Google Play (or its successors) operated by Google, Inc. or one of its
        affiliates (“Google”), you specifically acknowledge and agree that:
      </Text>
      <Text className={classes.text}>
        to the extent of any conflict between (a) the Google Play Terms of Services and the Google Play Business and
        Program Policies or such other terms which Google designates as default end user license terms for Google Play
        (all of which together are referred to as the “Google Play Terms”), and (b) the other terms and conditions in
        these Terms, the Google Play Terms shall apply with respect to your use of the Platform that you download from
        Google Play, and
      </Text>
      <Text className={classes.text}>
        you hereby acknowledge that Google does not have any responsibility or liability related to compliance or
        non-compliance by SWAP Integration or you (or any other user) under these Terms or the Google Play Terms.
      </Text>
      <Title className={classes.subtitle}>14. Contact Us</Title>
      <Text className={classes.text}>
        You can reach us at:{' '}
        <a href='https://www.swapintegration.com/contact'>https://www.swapintegration.com/contact</a> or write us at
        SWAP Integration, LLC 4672 W. 99th Place, Westminster, CO 80031, USA.
      </Text>
    </Container>
  )
}

export default TermsOfService
